// Per-agency brand color — Flowio's take on the Field Suite's boot-time
// brand system. The Field Suite rebrands a whole deployment from
// GET /api/brand; Flowio is multi-tenant, so the brand is a workspace
// setting (`brand_color`) applied to :root whenever the active agency's
// settings load. Every rule that reads --primary (nav active states,
// buttons, rings, chart-1) rebrands at once — override the variable,
// never a component.

const HEX_RE = /^#?([0-9a-fA-F]{6})$/;

export function normalizeHex(input: string | undefined | null): string | null {
  if (!input) return null;
  const m = String(input).trim().match(HEX_RE);
  return m ? `#${m[1].toLowerCase()}` : null;
}

function hexToRgb(hex: string): [number, number, number] {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function rgbToHex(r: number, g: number, b: number): string {
  const c = (x: number) => Math.max(0, Math.min(255, Math.round(x))).toString(16).padStart(2, "0");
  return `#${c(r)}${c(g)}${c(b)}`;
}

/** Shift toward black (amount 0..1). */
function darken(hex: string, amount: number): string {
  const [r, g, b] = hexToRgb(hex);
  return rgbToHex(r * (1 - amount), g * (1 - amount), b * (1 - amount));
}

/** Shift toward white (amount 0..1). */
function lighten(hex: string, amount: number): string {
  const [r, g, b] = hexToRgb(hex);
  return rgbToHex(r + (255 - r) * amount, g + (255 - g) * amount, b + (255 - b) * amount);
}

/** White or near-black text, whichever contrasts better on the color. */
function foregroundFor(hex: string): string {
  const [r, g, b] = hexToRgb(hex);
  // Perceptual luma (ITU-R BT.601)
  const luma = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luma > 0.6 ? "#0f172a" : "#ffffff";
}

const BRAND_VARS = [
  "--primary", "--primary-foreground", "--primary-hover",
  "--ring", "--accent", "--accent-foreground", "--chart-1",
] as const;

/**
 * Apply (or clear) the agency's brand color on :root. Pass null/invalid to
 * restore the stylesheet defaults (inline overrides are removed).
 */
export function applyBrandColor(hexInput: string | undefined | null): void {
  const root = document.documentElement;
  const hex = normalizeHex(hexInput);
  if (!hex) {
    for (const v of BRAND_VARS) root.style.removeProperty(v);
    return;
  }
  root.style.setProperty("--primary", hex);
  root.style.setProperty("--primary-foreground", foregroundFor(hex));
  root.style.setProperty("--primary-hover", darken(hex, 0.18));
  root.style.setProperty("--ring", hex);
  // Soft tint for selected/secondary surfaces (Field Suite's brand wash).
  root.style.setProperty("--accent", lighten(hex, 0.88));
  root.style.setProperty("--accent-foreground", darken(hex, 0.25));
  root.style.setProperty("--chart-1", hex);
}
